import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import {
  MedicalAppointment,
  MedicalAppointmentCategory,
  MedicalAppointmentType,
} from '@/types';
import {
  useCreateMedicalAppointment,
  useUpdateMedicalAppointment,
  useMedicalTypes,
} from '@/hooks/useMedicalAppointments';
import { usePatients, useCreatePatient } from '@/hooks/usePatients';
import { useCreateOwner } from '@/hooks/useOwners';
import { CATEGORY_LABELS } from '@/services/medicalAppointments';
import {
  User,
  Dog,
  Phone,
  Mail,
  MapPin,
  Plus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';

const medicalSchema = z.object({
  patient_id: z.string().min(1, 'Seleccione una mascota'),
  category: z.enum(['surgery', 'ultrasound'], {
    required_error: 'Seleccione una categoría',
  }),
  appointment_type_id: z.string().optional(),
  scheduled_date: z.string().min(1, 'La fecha es requerida'),
  scheduled_time: z.string().min(1, 'La hora es requerida'),
  duration_minutes: z.string().min(1, 'La duración es requerida'),
  pre_op_notes: z.string().optional(),
  notes: z.string().optional(),
});

// Schema para nuevo cliente + mascota
const newClientSchema = z.object({
  owner_first_name: z.string().min(1, 'El nombre es requerido'),
  owner_last_name: z.string().min(1, 'El apellido es requerido'),
  owner_phone: z.string().optional(),
  owner_email: z.string().email('Email inválido').optional().or(z.literal('')),
  owner_address: z.string().optional(),
  pet_name: z.string().min(1, 'El nombre de la mascota es requerido'),
  pet_species: z.string().min(1, 'La especie es requerida'),
  pet_breed: z.string().optional(),
  pet_sex: z.enum(['male', 'female', 'unknown']),
  pet_size: z.enum(['xs', 'small', 'medium', 'large', 'xl']).optional(),
});

type MedicalFormData = z.infer<typeof medicalSchema>;
type NewClientFormData = z.infer<typeof newClientSchema>;

interface MedicalAppointmentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  editingAppointment?: MedicalAppointment | null;
  preselectedDate?: string | null;
  preselectedTime?: string | null;
  categoryFilter?: MedicalAppointmentCategory | null;
}

const speciesOptions = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const sexOptions = [
  { value: 'male', label: 'Macho' },
  { value: 'female', label: 'Hembra' },
  { value: 'unknown', label: 'No especificado' },
];

const sizeOptions = [
  { value: '', label: 'Seleccionar' },
  { value: 'xs', label: '0-10 kg' },
  { value: 'small', label: '10-20 kg' },
  { value: 'medium', label: '20-30 kg' },
  { value: 'large', label: '30-40 kg' },
  { value: 'xl', label: '+40 kg' },
];

const durationOptions = [
  { value: '15', label: '15 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '45', label: '45 minutos' },
  { value: '60', label: '1 hora' },
  { value: '90', label: '1.5 horas' },
  { value: '120', label: '2 horas' },
  { value: '180', label: '3 horas' },
];

export default function MedicalAppointmentForm({
  onSuccess,
  onCancel,
  editingAppointment,
  preselectedDate,
  preselectedTime,
  categoryFilter,
}: MedicalAppointmentFormProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showMoreOwnerFields, setShowMoreOwnerFields] = useState(false);
  const [newClientError, setNewClientError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: patients = [], refetch: refetchPatients } = usePatients({ search: searchTerm || undefined });
  const createAppointment = useCreateMedicalAppointment();
  const updateAppointment = useUpdateMedicalAppointment();
  const createOwner = useCreateOwner();
  const createPatient = useCreatePatient();

  // Determine the initial category
  const initialCategory: MedicalAppointmentCategory =
    editingAppointment?.category || categoryFilter || 'surgery';

  // Fetch medical types (all active), filtered client-side by selected category
  const { data: allTypes = [] } = useMedicalTypes(undefined, true);

  // Main appointment form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MedicalFormData>({
    resolver: zodResolver(medicalSchema),
    defaultValues: {
      patient_id: editingAppointment?.patient_id?.toString() || '',
      category: initialCategory,
      appointment_type_id: editingAppointment?.appointment_type_id?.toString() || '',
      scheduled_date: editingAppointment?.scheduled_date || preselectedDate || '',
      scheduled_time: editingAppointment?.scheduled_time?.slice(0, 5) || preselectedTime || '09:00',
      duration_minutes: editingAppointment?.duration_minutes?.toString() || '60',
      pre_op_notes: editingAppointment?.pre_op_notes || '',
      notes: editingAppointment?.notes || '',
    },
  });

  // New client form
  const {
    register: registerNewClient,
    handleSubmit: handleSubmitNewClient,
    reset: resetNewClient,
    formState: { errors: newClientErrors },
  } = useForm<NewClientFormData>({
    resolver: zodResolver(newClientSchema),
    defaultValues: {
      owner_first_name: '',
      owner_last_name: '',
      owner_phone: '',
      owner_email: '',
      owner_address: '',
      pet_name: '',
      pet_species: 'Canino',
      pet_breed: '',
      pet_sex: 'unknown',
      pet_size: undefined,
    },
  });

  const selectedPatientId = watch('patient_id');
  const selectedCategory = watch('category');
  const selectedTypeId = watch('appointment_type_id');
  const selectedPatient = patients.find((p) => p.id.toString() === selectedPatientId);

  // Filter types by selected category
  const filteredTypes = allTypes.filter(
    (t: MedicalAppointmentType) => t.category === selectedCategory
  );

  // When category changes, reset the type selection if the currently selected type doesn't belong to the new category
  useEffect(() => {
    if (selectedTypeId) {
      const currentType = allTypes.find(
        (t: MedicalAppointmentType) => t.id.toString() === selectedTypeId
      );
      if (currentType && currentType.category !== selectedCategory) {
        setValue('appointment_type_id', '');
      }
    }
  }, [selectedCategory, selectedTypeId, allTypes, setValue]);

  // When a type is selected, auto-fill duration and pre-op notes from the type's defaults
  useEffect(() => {
    if (selectedTypeId) {
      const selectedType = allTypes.find(
        (t: MedicalAppointmentType) => t.id.toString() === selectedTypeId
      );
      if (selectedType) {
        setValue('duration_minutes', selectedType.default_duration_minutes.toString());
        // Auto-fill pre-op notes from type defaults (only if field is empty or was from a previous type)
        if (selectedType.default_pre_op_notes) {
          const currentNotes = watch('pre_op_notes') || '';
          // Only auto-fill if notes are empty or haven't been manually edited
          if (!currentNotes.trim()) {
            setValue('pre_op_notes', selectedType.default_pre_op_notes);
          }
        }
      }
    }
  }, [selectedTypeId, allTypes, setValue, watch]);

  // Reset form when key props change
  useEffect(() => {
    const cat: MedicalAppointmentCategory =
      editingAppointment?.category || categoryFilter || 'surgery';
    reset({
      patient_id: editingAppointment?.patient_id?.toString() || '',
      category: cat,
      appointment_type_id: editingAppointment?.appointment_type_id?.toString() || '',
      scheduled_date: editingAppointment?.scheduled_date || preselectedDate || '',
      scheduled_time: editingAppointment?.scheduled_time?.slice(0, 5) || preselectedTime || '09:00',
      duration_minutes: editingAppointment?.duration_minutes?.toString() || '60',
      pre_op_notes: editingAppointment?.pre_op_notes || '',
      notes: editingAppointment?.notes || '',
    });
  }, [editingAppointment, preselectedDate, preselectedTime, categoryFilter, reset]);

  const onSubmit = async (data: MedicalFormData) => {
    setSubmitError(null);
    try {
      if (editingAppointment) {
        await updateAppointment.mutateAsync({
          id: editingAppointment.id,
          data: {
            patient_id: parseInt(data.patient_id),
            appointment_type_id: data.appointment_type_id
              ? parseInt(data.appointment_type_id)
              : undefined,
            category: data.category as MedicalAppointmentCategory,
            scheduled_date: data.scheduled_date,
            scheduled_time: data.scheduled_time,
            duration_minutes: parseInt(data.duration_minutes),
            pre_op_notes: data.pre_op_notes || undefined,
            notes: data.notes || undefined,
          },
        });
      } else {
        await createAppointment.mutateAsync({
          patient_id: parseInt(data.patient_id),
          appointment_type_id: data.appointment_type_id
            ? parseInt(data.appointment_type_id)
            : undefined,
          category: data.category as MedicalAppointmentCategory,
          scheduled_date: data.scheduled_date,
          scheduled_time: data.scheduled_time,
          duration_minutes: parseInt(data.duration_minutes),
          pre_op_notes: data.pre_op_notes || undefined,
          notes: data.notes || undefined,
        });
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error saving medical appointment:', error);
      if (error?.response?.status === 409) {
        setSubmitError(
          error.response.data?.detail || 'Ya existe un turno médico en ese horario'
        );
      } else {
        setSubmitError(
          error?.response?.data?.detail || 'Error al guardar el turno médico'
        );
      }
    }
  };

  // Create new client + pet
  const onSubmitNewClient = async (data: NewClientFormData) => {
    setNewClientError(null);
    try {
      // 1. Create the owner
      const newOwner = await createOwner.mutateAsync({
        first_name: data.owner_first_name,
        last_name: data.owner_last_name,
        phone: data.owner_phone || undefined,
        email: data.owner_email || undefined,
        address: data.owner_address || undefined,
      });

      // 2. Create the pet
      const newPatient = await createPatient.mutateAsync({
        owner_id: newOwner.id,
        name: data.pet_name,
        species: data.pet_species,
        breed: data.pet_breed || undefined,
        sex: data.pet_sex,
        size: data.pet_size || undefined,
      });

      // 3. Refresh patient list and select the new one
      await refetchPatients();
      setValue('patient_id', newPatient.id.toString());

      // 4. Clean up and close the new client form
      resetNewClient();
      setShowNewClientForm(false);
      setShowMoreOwnerFields(false);
    } catch (err: any) {
      console.error('Error creating client:', err);
      setNewClientError(err?.response?.data?.detail || 'Error al crear el cliente');
    }
  };

  // Build type options with an empty first option
  const typeOptions = [
    { value: '', label: 'Sin tipo específico' },
    ...filteredTypes.map((t: MedicalAppointmentType) => ({
      value: t.id.toString(),
      label: t.name,
    })),
  ];

  const isCreatingClient = createOwner.isPending || createPatient.isPending;
  const isSurgery = selectedCategory === 'surgery';

  return (
    <div className="space-y-4">
      {/* New client form (collapsible) */}
      {showNewClientForm ? (
        <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary-600" />
              Nuevo Cliente y Mascota
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewClientForm(false)}
            >
              Cancelar
            </Button>
          </div>

          {newClientError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {newClientError}
            </div>
          )}

          <form onSubmit={handleSubmitNewClient(onSubmitNewClient)} className="space-y-4">
            {/* Owner data */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="h-4 w-4 text-taupe-600" />
                Datos del Propietario
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Nombre *"
                  placeholder="Nombre"
                  {...registerNewClient('owner_first_name')}
                  error={newClientErrors.owner_first_name?.message}
                />
                <Input
                  label="Apellido *"
                  placeholder="Apellido"
                  {...registerNewClient('owner_last_name')}
                  error={newClientErrors.owner_last_name?.message}
                />
              </div>

              <Input
                label="Teléfono"
                placeholder="Número de contacto"
                {...registerNewClient('owner_phone')}
                error={newClientErrors.owner_phone?.message}
              />

              <button
                type="button"
                onClick={() => setShowMoreOwnerFields(!showMoreOwnerFields)}
                className="flex items-center gap-1 text-sm text-taupe-600 hover:text-taupe-800"
              >
                {showMoreOwnerFields ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Ocultar campos adicionales
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Más campos (Email, Dirección)
                  </>
                )}
              </button>

              {showMoreOwnerFields && (
                <div className="space-y-3">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="email@ejemplo.com"
                    {...registerNewClient('owner_email')}
                    error={newClientErrors.owner_email?.message}
                  />
                  <Input
                    label="Dirección"
                    placeholder="Dirección"
                    {...registerNewClient('owner_address')}
                  />
                </div>
              )}
            </div>

            {/* Pet data */}
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Dog className="h-4 w-4 text-cream-700" />
                Datos de la Mascota
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Nombre *"
                  placeholder="Nombre de la mascota"
                  {...registerNewClient('pet_name')}
                  error={newClientErrors.pet_name?.message}
                />
                <Select
                  label="Especie *"
                  options={speciesOptions}
                  {...registerNewClient('pet_species')}
                  error={newClientErrors.pet_species?.message}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="Raza"
                  placeholder="Ej: Labrador"
                  {...registerNewClient('pet_breed')}
                />
                <Select
                  label="Sexo"
                  options={sexOptions}
                  {...registerNewClient('pet_sex')}
                />
                <Select
                  label="Tamaño"
                  options={sizeOptions}
                  {...registerNewClient('pet_size')}
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" loading={isCreatingClient}>
                <Plus className="h-4 w-4 mr-1" />
                Crear y Seleccionar
              </Button>
            </div>
          </form>
        </div>
      ) : (
        /* Patient search and selection */
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Buscar mascota</label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewClientForm(true)}
              className="text-primary-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuevo Cliente
            </Button>
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre de mascota o dueño..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
          />
          <Select
            label="Mascota"
            error={errors.patient_id?.message}
            {...register('patient_id')}
            options={patients.map((p) => ({
              value: p.id.toString(),
              label: `${p.name} (${p.species}${p.breed ? ` - ${p.breed}` : ''}) - ${p.owner?.first_name} ${p.owner?.last_name}`,
            }))}
          />

          {/* Selected owner info */}
          {selectedPatient && selectedPatient.owner && (
            <div className="mt-3 p-3 bg-taupe-50 rounded-lg border border-taupe-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-taupe-600" />
                <span className="font-medium text-gray-900">
                  {selectedPatient.owner.first_name} {selectedPatient.owner.last_name}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {selectedPatient.owner.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.phone}
                  </div>
                )}
                {selectedPatient.owner.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.email}
                  </div>
                )}
                {selectedPatient.owner.address && (
                  <div className="flex items-center gap-2 text-gray-600 sm:col-span-2">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.address}
                  </div>
                )}
              </div>
              {/* Pet info */}
              <div className="mt-2 pt-2 border-t border-taupe-200 text-sm text-gray-600">
                <span className="font-medium">{selectedPatient.name}</span>
                {' - '}
                {selectedPatient.species}
                {selectedPatient.breed && ` (${selectedPatient.breed})`}
                {selectedPatient.sex && selectedPatient.sex !== 'unknown' && (
                  <span className="ml-2">
                    {selectedPatient.sex === 'male' ? 'Macho' : 'Hembra'}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Appointment form */}
      {!showNewClientForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Overlap error */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <span className="text-red-500 font-bold">!</span>
              {submitError}
            </div>
          )}

          {/* Category selector - radio buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoría *
            </label>
            <div className="flex gap-4">
              {(Object.entries(CATEGORY_LABELS) as [MedicalAppointmentCategory, string][]).map(
                ([value, label]) => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                      selectedCategory === value
                        ? value === 'surgery'
                          ? 'border-red-400 bg-red-50 text-red-800'
                          : 'border-purple-400 bg-purple-50 text-purple-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      value={value}
                      {...register('category')}
                      className="sr-only"
                    />
                    <span className="font-medium">{label}</span>
                  </label>
                )
              )}
            </div>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>

          {/* Type selector */}
          <Select
            label="Tipo de Procedimiento"
            error={errors.appointment_type_id?.message}
            {...register('appointment_type_id')}
            options={typeOptions}
          />

          {/* Date and time */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Fecha"
              type="date"
              error={errors.scheduled_date?.message}
              {...register('scheduled_date')}
            />
            <Input
              label="Hora"
              type="time"
              error={errors.scheduled_time?.message}
              {...register('scheduled_time')}
            />
          </div>

          {/* Duration */}
          <Select
            label="Duración"
            error={errors.duration_minutes?.message}
            {...register('duration_minutes')}
            options={durationOptions}
          />

          {/* Pre-op notes - prominent for surgery, optional for ultrasound */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              {isSurgery && <AlertTriangle className="h-4 w-4 text-amber-600" />}
              Indicaciones Pre-quirúrgicas
              {isSurgery ? ' *' : ' (opcional)'}
            </label>
            <textarea
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                isSurgery
                  ? 'border-amber-300 bg-amber-50 placeholder-amber-500'
                  : 'border-gray-300 bg-white'
              }`}
              rows={isSurgery ? 4 : 2}
              placeholder={
                isSurgery
                  ? 'Ej: Ayuno de 12 horas, suspender medicación anticoagulante, traer estudios previos...'
                  : 'Indicaciones previas al estudio (opcional)'
              }
              {...register('pre_op_notes')}
            />
            {isSurgery && (
              <p className="mt-1 text-xs text-amber-700">
                Estas indicaciones serán comunicadas al propietario antes del procedimiento.
              </p>
            )}
          </div>

          {/* General notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Observaciones adicionales sobre el turno..."
              {...register('notes')}
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={
                isSubmitting || createAppointment.isPending || updateAppointment.isPending
              }
            >
              {editingAppointment ? 'Guardar Cambios' : 'Crear Turno'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
